import { useState, useEffect } from 'react';
import { Clock, CheckCircle2, Circle, Pause } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { Task, WorkOrder } from '../../types/workOrders';
import {
    startTask,
    endTask,
    pauseTask,
    resumeTask,
    getCurrentShiftCached,
} from '../../utils/api';
import {
    getTaskSession,
    setTaskSession,
    updateTaskSession,
    removeTaskSession,
} from '../../utils/firebase';
import {
    sendTelegramMessage,
    formatTaskStartMessage,
    formatTaskEndMessage,
} from '../../utils/telegram';
import { calculateDuration } from '../../utils/time';
import { TaskTimestamps } from './TaskTimestamps';
import { TaskDescription } from './TaskDescription';
import { TaskActions } from './TaskActions';
import { TaskConfirmationDialog } from './TaskConfirmationDialog';
import { TaskPhotoGallery } from './TaskPhotoGallery';

interface TaskItemProps {
    task: Task;
    workOrder: WorkOrder;
    onTaskUpdate: () => void;
    isLast?: boolean;
}

function startShiftPollingOnce(
    key: string,
    fn: () => void,
    intervalMs: number,
) {
    const g = globalThis as any;

    if (!g.__shiftPolling) g.__shiftPolling = {};
    if (g.__shiftPolling[key]) return;

    g.__shiftPolling[key] = setInterval(fn, intervalMs);
}

export function TaskItem({
    task,
    workOrder,
    onTaskUpdate,
    isLast,
}: TaskItemProps) {
    const [loading, setLoading] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState<
        'start' | 'complete' | null
    >(null);
    const [showPhotoGallery, setShowPhotoGallery] = useState(false);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    // Текущий активный сегмент (когда реально идёт работа)
    const [startTime, setStartTime] = useState<number | null>(null);

    // Сколько активного времени накоплено до текущего сегмента (без пауз)
    const [accumulatedMs, setAccumulatedMs] = useState<number>(0);

    // Текущее отображаемое время (accumulated + running segment)
    const [elapsedTime, setElapsedTime] = useState<number>(0);

    const [intervalId, setIntervalId] = useState<NodeJS.Timer | null>(null);

    const [isTaskCompleted, setIsTaskCompleted] = useState(task.isCompleted);
    const [isPaused, setIsPaused] = useState(false);
    const [canStartTask, setCanStartTask] = useState(false);

    const { accessToken, workerId, workerInfo } = useAuthStore();

    const isWorkOrderCompleted = ['COMPLETED', 'REJECTED'].includes(
        workOrder.status,
    );

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        setIsTaskCompleted(task.isCompleted);
    }, [task.isCompleted]);

    useEffect(() => {
        if (workerId && task.id && !isWorkOrderCompleted) {
            void loadTaskSession();
        }
    }, [workerId, isWorkOrderCompleted, accessToken]);

    useEffect(() => {
        if (isWorkOrderCompleted || isTaskCompleted) return;

        startShiftPollingOnce(
            `worker:${workerId}`,
            () => {
                void checkShiftStatus();
            },
            30000,
        );
    }, [workerId, isWorkOrderCompleted, isTaskCompleted]);

    const startTicker = (segmentStart: number, baseAccumulated: number) => {
        if (intervalId) clearInterval(intervalId);

        const interval = setInterval(() => {
            setElapsedTime(baseAccumulated + (Date.now() - segmentStart));
        }, 1000);

        setIntervalId(interval);
    };

    const stopTicker = () => {
        if (intervalId) {
            clearInterval(intervalId);
            setIntervalId(null);
        }
    };

    const loadTaskSession = async () => {
        if (!workerId) return;

        try {
            const taskSession = await getTaskSession(workerId, task.id);

            const base = taskSession?.accumulated_ms || 0;
            setAccumulatedMs(base);

            // Если таск на паузе
            if (taskSession?.status === 'paused') {
                setIsPaused(true);
                setStartTime(null);
                setElapsedTime(base);
                stopTicker();
                return;
            }

            // Если таск в прогрессе (есть активный сегмент)
            if (
                taskSession?.start_time &&
                taskSession?.status === 'in_progress'
            ) {
                setIsPaused(false);
                setStartTime(taskSession.start_time);
                setElapsedTime(base + (Date.now() - taskSession.start_time));
                startTicker(taskSession.start_time, base);
            } else {
                // Нет активного сегмента
                setStartTime(null);
                setElapsedTime(base);
                stopTicker();
            }
        } catch (error) {
            console.error('Error loading task session:', error);
        }
    };

    const checkShiftStatus = async () => {
        if (!workerId || !accessToken) return;

        try {
            const currentShift = await getCurrentShiftCached(
                accessToken,
                parseInt(workerId, 10),
                false,
            );

            const isActiveShift =
                currentShift &&
                !currentShift.finishjob_time &&
                !currentShift.clockout_time;

            setCanStartTask(Boolean(isActiveShift));
        } catch (error) {
            console.error('Error checking shift status:', error);
            setCanStartTask(false);
        }
    };

    const handleStartOrResumeTask = async () => {
        if (!workerId || !accessToken) return;
        setShowConfirmation(null);

        if (!canStartTask) {
            toast.error('You must be clocked in to start tasks');
            return;
        }
        if (isOffline) {
            toast.error('You must be online to start tasks');
            return;
        }

        setLoading(true);
        try {
            // Double check current shift with backend
            const currentShift = await getCurrentShiftCached(
                accessToken,
                parseInt(workerId, 10),
                true,
            );

            const isActiveShift =
                currentShift &&
                !currentShift.finishjob_time &&
                !currentShift.clockout_time;

            if (!isActiveShift) {
                toast.error('You must be clocked in to start tasks');
                return;
            }

            const now = Date.now();

            // Если до этого был paused — используем resumeTask
            if (isPaused) {
                await resumeTask(
                    accessToken,
                    parseInt(workerId, 10),
                    task.id,
                    now,
                );
            } else {
                await startTask(
                    accessToken,
                    parseInt(workerId, 10),
                    task.id,
                    now,
                );
            }

            setIsPaused(false);
            setStartTime(now);

            // Firebase session
            // если сессии нет — setTaskSession; если есть — updateTaskSession тоже ок,
            // но чтобы не ломать вашу текущую структуру, делаем setTaskSession (как раньше)
            await setTaskSession({
                worker_id: workerId,
                task_id: task.id,
                description: task.desc,
                wo_number: workOrder.wo_number,
                report_id: workOrder.report_id,
                start_time: now,
                status: 'in_progress',
                accumulated_ms: accumulatedMs,
            });

            // Таймер
            startTicker(now, accumulatedMs);

            if (workerInfo?.groupId) {
                const message = formatTaskStartMessage(
                    workerInfo,
                    task,
                    workOrder,
                );
                await sendTelegramMessage(workerInfo.groupId, message);
            }

            onTaskUpdate();
            toast.success(
                isPaused
                    ? 'Task resumed successfully'
                    : 'Task started successfully',
            );
        } catch (error) {
            console.error('Task start/resume error:', error);
            toast.error(
                isPaused ? 'Failed to resume task' : 'Failed to start task',
            );

            setStartTime(null);
            stopTicker();

            // Не удаляем сессию при resume/start ошибке без уверенности,
            // но ваш старый код удалял — оставлю поведение как раньше:
            await removeTaskSession(workerId, task.id);
        } finally {
            setLoading(false);
        }
    };

    const handlePauseTask = async () => {
        if (!workerId || !accessToken) return;
        if (!startTime) return;

        if (isOffline) {
            toast.error('You must be online to pause tasks');
            return;
        }

        setLoading(true);
        try {
            const pauseTime = Date.now();
            const segmentMs = pauseTime - startTime;
            const newAccumulated = accumulatedMs + segmentMs;

            await pauseTask(
                accessToken,
                parseInt(workerId, 10),
                task.id,
                pauseTime,
            );

            // Обновляем UI
            setAccumulatedMs(newAccumulated);
            setElapsedTime(newAccumulated);
            setStartTime(null);
            setIsPaused(true);
            stopTicker();

            // Firebase
            await updateTaskSession(workerId, task.id, {
                status: 'paused',
                accumulated_ms: newAccumulated,
                start_time: null,
            });

            toast.success('Task paused');
        } catch (error) {
            console.error('Pause task error:', error);
            toast.error('Failed to pause task');
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteTask = async () => {
        if (!workerId || !accessToken) return;
        setShowConfirmation(null);

        if (isOffline) {
            toast.error('You must be online to complete tasks');
            return;
        }

        // НЕЛЬЗЯ завершать, если задача на паузе
        if (isPaused || !startTime) {
            toast.error('Resume the task before completing it');
            return;
        }

        setLoading(true);

        try {
            const endTime = Date.now();
            const durationSec = Math.floor((endTime - startTime) / 1000);

            // Минимальное время выполнения — как было раньше
            if (durationSec < 10) {
                toast.error('Task must run for at least 10 seconds');
                return;
            }

            await endTask(
                accessToken,
                parseInt(workerId, 10),
                task.id,
                startTime,
                endTime,
            );

            // Firebase — закрываем сессию
            await updateTaskSession(workerId, task.id, { status: 'completed' });
            await removeTaskSession(workerId, task.id);

            // Telegram
            if (workerInfo?.groupId) {
                const message = formatTaskEndMessage(
                    workerInfo,
                    task,
                    workOrder,
                    calculateDuration(startTime, endTime),
                );
                await sendTelegramMessage(workerInfo.groupId, message);
            }

            // UI cleanup
            if (intervalId) {
                clearInterval(intervalId);
                setIntervalId(null);
            }

            setIsTaskCompleted(true);
            setIsPaused(false);
            setStartTime(null);
            setElapsedTime(0);

            onTaskUpdate();
            toast.success('Task completed successfully');
        } catch (error) {
            console.error('Complete task error:', error);
            toast.error('Failed to complete task');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className={`bg-light-card dark:bg-dark-card rounded-lg p-6 shadow-soft dark:shadow-soft-dark border border-light-border dark:border-dark-border ${
                !isLast && 'mb-4'
            }`}
        >
            <div className='flex items-start justify-between gap-4'>
                <div className='flex-1 min-w-0'>
                    {/* Status Badge */}
                    <div className='flex items-center gap-2 mb-3'>
                        {isTaskCompleted ? (
                            <div className='flex items-center gap-2 text-green-600 dark:text-green-400'>
                                <CheckCircle2 className='h-5 w-5' />
                                <span className='text-sm font-medium'>
                                    Completed
                                </span>
                            </div>
                        ) : startTime ? (
                            <div className='flex items-center gap-2 text-blue-600 dark:text-blue-400'>
                                <Clock className='h-5 w-5' />
                                <span className='text-sm font-medium'>
                                    In Progress
                                </span>
                            </div>
                        ) : isPaused ? (
                            <div className='flex items-center gap-2 text-yellow-600 dark:text-yellow-400'>
                                <Pause className='h-5 w-5' />
                                <span className='text-sm font-medium'>
                                    Paused
                                </span>
                            </div>
                        ) : (
                            <div className='flex items-center gap-2 text-gray-500 dark:text-gray-400'>
                                <Circle className='h-5 w-5' />
                                <span className='text-sm font-medium'>
                                    Not Started
                                </span>
                            </div>
                        )}
                    </div>

                    <TaskDescription task={task} />
                </div>

                <TaskActions
                    task={task}
                    workOrder={workOrder}
                    isTaskCompleted={isTaskCompleted}
                    isWorkOrderCompleted={isWorkOrderCompleted}
                    startTime={startTime}
                    loading={loading}
                    isPaused={isPaused}
                    onPause={handlePauseTask}
                    onResume={() => setShowConfirmation('start')}
                    onShowGallery={() => setShowPhotoGallery(true)}
                    onShowConfirmation={(t) => {
                        if (t === 'start') setShowConfirmation('start');
                        if (t === 'end') setShowConfirmation('complete');
                    }}
                />
            </div>

            <TaskTimestamps
                timeRecords={task.TaskTimeRecords}
                currentStartTime={startTime}
                elapsedTime={elapsedTime}
                isCompleted={isTaskCompleted}
            />

            {showConfirmation && (
                <TaskConfirmationDialog
                    type={showConfirmation === 'start' ? 'start' : 'end'}
                    onConfirm={
                        showConfirmation === 'start'
                            ? handleStartOrResumeTask
                            : handleCompleteTask
                    }
                    onCancel={() => setShowConfirmation(null)}
                    loading={loading}
                />
            )}

            {showPhotoGallery && (
                <TaskPhotoGallery
                    task={task}
                    workOrder={workOrder}
                    onClose={() => setShowPhotoGallery(false)}
                />
            )}
        </div>
    );
}
